/**
 * Kombinierte Karte „Benachrichtigungen" — ein Eintrag mit Zusammenfassung und
 * einem Bearbeiten-Menü (Modal), in dem Push-Aktivierung, Erinnerungs-Vorlauf
 * und Vortags-Erinnerung zusammen verwaltet werden.
 *
 * Alle Änderungen wirken sofort:
 *   - Push:      An-/Abmelden über den Push-Hook (usePush).
 *   - Vorlauf/Vortag: PUT /api/me/preferences, aktualisiert den Auth-Store.
 */

import { useState } from 'react';
import { useAuth, type AuthUser } from '../store/auth';
import { apiFetch, ApiError } from '../lib/api';
import { usePush } from '../data/push';
import { hapticError, hapticSuccess } from '../lib/haptics';

/** Erlaubte Werte (in Minuten) für die Stunden-Erinnerung. */
const PRESETS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'Aus' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 h' },
  { value: 120, label: '2 h' },
  { value: 360, label: '6 h' },
];

function reminderLabel(minutes: number): string {
  return PRESETS.find((p) => p.value === minutes)?.label ?? `${minutes} min`;
}

export function NotificationsCard() {
  const user = useAuth((s) => s.user);
  const token = useAuth((s) => s.token);
  const [open, setOpen] = useState(false);
  const push = usePush();

  if (!user || !token) return null;

  const pushSummary = !push.supported
    ? 'Push nicht unterstützt'
    : push.permission === 'denied'
      ? 'Push blockiert'
      : push.subscribed
        ? 'Push an'
        : 'Push aus';

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">Benachrichtigungen</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {pushSummary} · Erinnerung{' '}
            {user.reminderMinutesBefore === 0
              ? 'aus'
              : reminderLabel(user.reminderMinutesBefore) + ' vorher'}{' '}
            · Vortags {user.reminderDayBefore ? 'an' : 'aus'}
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="btn-ghost text-xs px-3 py-1.5 shrink-0"
        >
          Bearbeiten
        </button>
      </div>

      {open && <NotificationsModal user={user} token={token} onClose={() => setOpen(false)} />}
    </div>
  );
}

function NotificationsModal({
  user,
  token,
  onClose,
}: {
  user: AuthUser;
  token: string;
  onClose: () => void;
}) {
  const setUser = useAuth((s) => s.setUser);
  const push = usePush();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(
    patch: Partial<Pick<AuthUser, 'reminderMinutesBefore' | 'reminderDayBefore'>>,
  ) {
    setBusy(true);
    setError(null);
    try {
      const data = await apiFetch<{ user: AuthUser }>('/api/me/preferences', {
        method: 'PUT',
        token,
        body: patch,
      });
      setUser(data.user);
      hapticSuccess();
    } catch (err) {
      hapticError();
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Speichern fehlgeschlagen',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-surface-2 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-md max-h-[92vh] sm:max-h-[90vh] overflow-y-auto border border-border/60 space-y-5">
        <h3 className="text-lg font-semibold">Benachrichtigungen</h3>

        {/* Push */}
        <div>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-slate-300">Push-Benachrichtigungen</p>
              <p className="text-xs text-slate-500">
                Vortags- und 1-h-Erinnerung sowie Hinweise auf neu geplante Schichten.
              </p>
            </div>
            {push.supported && push.permission !== 'denied' && (
              <button
                onClick={() => (push.subscribed ? push.disable() : push.enable())}
                disabled={push.busy}
                className={`text-xs px-3 py-2 rounded font-medium disabled:opacity-50 shrink-0 ${
                  push.subscribed
                    ? 'bg-surface-3 hover:bg-slate-600'
                    : 'bg-brand hover:bg-brand-dark text-white'
                }`}
              >
                {push.busy ? '…' : push.subscribed ? 'Deaktivieren' : 'Aktivieren'}
              </button>
            )}
          </div>
          {!push.supported && (
            <p className="text-xs text-slate-500 mt-1">
              Dein Browser unterstützt keine Web-Push. Auf Android die App als PWA installieren
              („Zum Startbildschirm hinzufügen").
            </p>
          )}
          {push.permission === 'denied' && (
            <p className="text-xs text-amber-300 mt-1">
              Benachrichtigungen sind für diese Seite blockiert. In den Browser-Einstellungen
              erlauben, dann Seite neu laden.
            </p>
          )}
          {push.error && <p className="text-xs text-red-400 mt-1">{push.error}</p>}
        </div>

        <div className="border-t border-border/60 pt-4">
          <p className="text-sm text-slate-300 mb-1">Erinnerungs-Vorlauf</p>
          <p className="text-xs text-slate-500 mb-3">
            Wie weit im Voraus möchtest du an deine nächste Schicht erinnert werden?
          </p>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => {
              const active = user.reminderMinutesBefore === p.value;
              return (
                <button
                  key={p.value}
                  disabled={busy || active}
                  onClick={() => void save({ reminderMinutesBefore: p.value })}
                  className={`text-xs px-3 py-1.5 rounded font-medium disabled:cursor-not-allowed ${
                    active
                      ? 'bg-brand text-white'
                      : 'bg-surface-3 hover:bg-slate-600 disabled:opacity-50'
                  }`}
                  aria-pressed={active}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="border-t border-border/60 pt-4">
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <div>
              <p className="text-sm text-slate-300">Vortags-Erinnerung</p>
              <p className="text-xs text-slate-500">
                Hinweis um 18:00 Uhr, wenn am nächsten Tag eine Schicht ansteht.
              </p>
            </div>
            <input
              type="checkbox"
              checked={user.reminderDayBefore}
              disabled={busy}
              onChange={(e) => void save({ reminderDayBefore: e.target.checked })}
              className="w-5 h-5 accent-brand"
            />
          </label>
        </div>

        {error && <p className="text-sm text-red-400">{error}</p>}

        <div className="flex justify-end pt-1">
          <button type="button" onClick={onClose} className="btn">
            Fertig
          </button>
        </div>
      </div>
    </div>
  );
}
